import React from 'react';
import { useTranslation } from 'react-i18next';

import { TGroupInfoProps } from '@/console/features/groups/components/group-list/group-info/group-info.types';
import { GroupSyncStatus } from '@/console/features/groups/components/group-sync-status/group-sync-status';
import { useGroupResourcesCount } from '@/console/features/groups/hooks/use-group-resource-lists/use-group-resources-count';
import { useGroupedResourceElements } from '@/console/features/groups/hooks/use-grouped-resources-elements/use-grouped-resources-elements';
import { GroupInfo as SharedGroupInfo } from '@/shared/features/groups/components/group-info/group-info';
import { TGroupInfoResource } from '@/shared/features/groups/components/group-info/group-info.types';
import { DeviceSyncStatusEnum } from '@/shared/types/types.generated';

export const GroupInfo: React.FC<TGroupInfoProps> = ({
  groupId,
  syncStatus,
}) => {
  const { t } = useTranslation();
  const {
    subGroupsCount,
    devicesCount,
    projectsCount,
    gatewaysCount,
    documentsCount,
    pipelinesCount,
    vaultsCount,
    usersCount,
    rolesCount,
    licensesCount,
    integrationsCount,
  } = useGroupResourcesCount(groupId);
  const { getIcon } = useGroupedResourceElements();

  const groupSyncStatus = syncStatus?.groupSyncStatus;
  const hideSyncStatus =
    !groupSyncStatus || groupSyncStatus === DeviceSyncStatusEnum.Unknown;

  const iconProps = { size: '1rem', variant: 'light' } as const;
  const resources: TGroupInfoResource[] = [
    {
      key: 'ResourceGroupResponseType',
      label: t('groups:subgroups', { count: subGroupsCount }),
      count: subGroupsCount,
      icon: getIcon('ResourceGroupResponseType', iconProps),
    },
    {
      key: 'DeviceResponseType',
      label: t('groups:devices', { count: devicesCount }),
      count: devicesCount,
      icon: getIcon('DeviceResponseType', iconProps),
    },
    {
      key: 'ProjectResponseType',
      label: t('groups:projects', { count: projectsCount }),
      count: projectsCount,
      icon: getIcon('ProjectResponseType', iconProps),
    },
    {
      key: 'GatewayResponseType',
      label: t('groups:gateways', { count: gatewaysCount }),
      count: gatewaysCount,
      icon: getIcon('GatewayResponseType', iconProps),
    },
    {
      key: 'DocumentResponseType',
      label: t('groups:documents', { count: documentsCount }),
      count: documentsCount,
      icon: getIcon('DocumentResponseType', iconProps),
    },
    {
      key: 'PipelineConfigurationResponseType',
      label: t('groups:pipelines', { count: pipelinesCount }),
      count: pipelinesCount,
      icon: getIcon('PipelineConfigurationResponseType', iconProps),
    },
    {
      key: 'VaultResponseType',
      label: t('groups:vaults', { count: vaultsCount }),
      count: vaultsCount,
      icon: getIcon('VaultResponseType', iconProps),
    },
    {
      key: 'UserResponseType',
      label: t('groups:users', { count: usersCount }),
      count: usersCount,
      icon: getIcon('UserResponseType', iconProps),
    },
    {
      key: 'UserRoleResponseType',
      label: t('groups:roles', { count: rolesCount }),
      count: rolesCount,
      icon: getIcon('UserRoleResponseType', iconProps),
    },
    {
      key: 'LicenseResponseType',
      label: t('groups:licenses', { count: licensesCount }),
      count: licensesCount,
      icon: getIcon('LicenseResponseType', iconProps),
    },
    {
      key: 'IntegrationResponseType',
      label: t('groups:integrations', { count: integrationsCount }),
      count: integrationsCount,
      icon: getIcon('IntegrationResponseType', iconProps),
    },
  ];

  return (
    <SharedGroupInfo
      resources={resources}
      syncStatus={
        !hideSyncStatus ? (
          <GroupSyncStatus
            status={groupSyncStatus}
            icon
            deviceInformation={syncStatus?.devicesSyncStatus?.map(
              ({ deviceId, deviceName, syncStatusTimestamp, syncStatus }) => ({
                deviceId,
                deviceName,
                syncStatusTimestamp,
                syncStatus,
              }),
            )}
          />
        ) : undefined
      }
    />
  );
};
